
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

type BusinessContextType = {
  userBusinessId: string | null;
  isBusinessOwner: boolean;
  isLoading: boolean;
  refreshBusinessData: () => Promise<void>;
};

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

type BusinessProviderProps = {
  children: ReactNode;
};

export const BusinessProvider: React.FC<BusinessProviderProps> = ({ children }) => {
  const [userBusinessId, setUserBusinessId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const fetchUserBusiness = async () => {
    if (!user) {
      setUserBusinessId(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      console.log('[BusinessContext] Fetching user business for user:', user.id);
      const { data, error } = await supabase
        .from('businesses')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[BusinessContext] Error fetching user business:', error);
        setUserBusinessId(null);
      } else {
        const businessId = data?.id || null;
        console.log('[BusinessContext] User business data:', businessId ? `Found: ${businessId}` : 'No business found');
        setUserBusinessId(businessId);
      }
    } catch (error) {
      console.error('[BusinessContext] Error in fetchUserBusiness:', error);
      setUserBusinessId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshBusinessData = async () => {
    console.log('[BusinessContext] Refreshing business data...');
    try {
      await fetchUserBusiness();
      // Also invalidate the related queries to ensure UI consistency
      queryClient.invalidateQueries({ queryKey: ['userBusiness'] });
      console.log('[BusinessContext] Business data refresh complete');
      return Promise.resolve();
    } catch (error) {
      console.error('[BusinessContext] Error refreshing business data:', error);
      return Promise.reject(error);
    }
  };

  useEffect(() => {
    fetchUserBusiness();
  }, [user]);

  const isBusinessOwner = !!userBusinessId;

  return (
    <BusinessContext.Provider
      value={{
        userBusinessId,
        isBusinessOwner,
        isLoading,
        refreshBusinessData
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};
